import type { LucideIcon } from 'lucide-react';
import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  'aria-label': string;
  /** Equal-width segments filling the container. */
  fullWidth?: boolean;
  /**
   * Render only each option's icon (UAT-1: the labelled theme control overflowed the sidebar).
   * The label stays the radio's accessible name (aria-label) and its hover tooltip (title).
   * Every option must have an icon when this is set.
   */
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

/**
 * The original app's segmented control (Day/Week toggle), with ARIA radio-group semantics:
 * one tab stop, arrow keys move (and wrap) the selection and the focus.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  fullWidth = false,
  iconOnly = false,
  disabled = false,
  className,
}: SegmentedControlProps<T>): JSX.Element {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = NEXT_KEYS.has(event.key) ? 1 : PREV_KEYS.has(event.key) ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const nextIndex = (index + step + options.length) % options.length;
    const next = options[nextIndex];
    if (!next) return;
    onChange(next.value);
    refs.current[nextIndex]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex rounded-lg border border-border bg-muted p-0.5', fullWidth && 'flex w-full', className)}
    >
      {options.map(({ value: optionValue, label, icon: Icon }, index) => {
        const selected = optionValue === value;
        return (
          <button
            key={optionValue}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={iconOnly ? label : undefined}
            title={iconOnly ? label : undefined}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(optionValue)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'inline-flex min-w-0 items-center justify-center gap-1.5 rounded-md py-1 text-xs transition-colors',
              iconOnly ? 'px-2' : 'px-3',
              'focus-visible:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
              selected ? 'bg-card font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
              fullWidth && 'flex-1',
            )}
          >
            {Icon && <Icon className={iconOnly ? 'h-4 w-4' : 'h-3.5 w-3.5'} aria-hidden="true" />}
            {!iconOnly && label}
          </button>
        );
      })}
    </div>
  );
}
